import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DoDisturbOutlinedIcon from "@mui/icons-material/DoDisturbOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro-7";
import { format, isPast } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";

import TimeRangeBar from "../../../design/time-range-bar";
import JobRoleChip from "../../../design/ui/job-role-chip";
import { formatDate } from "../../../utils/helpers";
import Highlight from "../../Highlight";
import workerNoShow from "../assets/worker_no_show.png";

interface IStatusObjType {
	[key: string]: {
		title: string;
		color: string;
		icon: JSX.Element;
	};
}

const useDayActivityColumn = (search: string) => {
	const { t } = useTranslation();
	const statusObj: IStatusObjType = {
		p: {
			title: t("worker-activity_status-present"),
			color: "#017374",
			icon: <CheckCircleOutlinedIcon color='success' />,
		},
		a: {
			title: t("worker-activity_status-no-show"),
			color: "#b00020",
			icon: (
				<img
					src={workerNoShow}
					alt='worker_no_show'
					style={{ color: "#1B5E20" }}
				/>
			),
		},
		t: {
			title: t("worker-activity_status-tardy"),
			color: "#01a299",
			icon: <AccessTimeOutlinedIcon color='warning' />,
		},
		e: {
			title: t("worker-activity_status-excused-absence"),
			color: "#7d5260",
			icon: <PauseCircleOutlinedIcon color='info' />,
		},
		x: {
			title: t("worker-activity_status-excused-tardy"),
			color: "#7d5260",
			icon: <PauseCircleOutlinedIcon color='info' />,
		},
		cancelled: {
			title: t("worker-activity_status-cancelled"),
			color: "#b00020",
			icon: <DoDisturbOutlinedIcon color='error' />,
		},
	};
	const columns: GridColDef[] = [
		{
			field: "workerName",
			headerName: t("worker-activity_worker-name"),
			flex: 1,
			minWidth: 165,
			hideable: true,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_worker-name")}</Typography>
			),
			renderCell: (params) => {
				const workerName = params.row.workerName;
				return (
					<Tooltip title={workerName}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workerName !== "" ? workerName : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					workerName: string;
					id: string;
				}
			) => {
				return row.workerName + "-" + row.id;
			},
		},
		{
			field: "workerTypeText",
			headerName: t("worker-activity_worker-type"),
			flex: 1,
			minWidth: 115,
			hideable: true,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_worker-type")}</Typography>
			),
			renderCell: (params) => {
				const workerType = params.row.workerTypeText;
				return (
					<Tooltip title={workerType}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workerType !== "" ? workerType : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					workerTypeText: string;
					id: string;
				}
			) => {
				return row.workerTypeText + "-" + row.id;
			},
		},
		{
			field: "workerSubTypeText",
			headerName: t("worker-activity_worker-sub-type"),
			flex: 1,
			minWidth: 130,
			hideable: true,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_worker-sub-type")}</Typography>
			),
			renderCell: (params) => {
				const workerSubType = params.row.workerSubTypeText;
				return (
					<Tooltip title={workerSubType}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workerSubType !== "" ? workerSubType : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					workerSubTypeText: string;
					id: string;
				}
			) => {
				return row.workerSubTypeText + "-" + row.id;
			},
		},
		{
			field: "shiftStartDate",
			headerName: t("worker-activity_shift-date"),
			flex: 1,
			minWidth: 165,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_shift-date")}</Typography>
			),
			renderCell: (params) => {
				if (params.row.isShiftAvailable) {
					const shiftStartDate = params.row.shiftStartDateTime;
					const startDate = format(new Date(shiftStartDate), "MM/dd/yyyy");
					return (
						<Tooltip title={startDate}>
							<span
								style={{
									textOverflow: "ellipsis",
									overflow: "hidden",
								}}
							>
								<Highlight
									searchText={search}
									value={startDate !== "" ? startDate : "-"}
								/>
							</span>
						</Tooltip>
					);
				}
				return "-";
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					shiftStartDateTime: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					const shiftStartDate = row.shiftStartDateTime;
					const startDate = formatDate({
						dateStr: shiftStartDate,
						formatType: "NUMERIC_DATE",
					});
					return startDate + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "shiftHours",
			headerName: t("worker-activity_shift-hours"),
			flex: 1,
			minWidth: 165,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_shift-time")}</Typography>
			),
			renderCell: (params) => {
				if (params.row.isShiftAvailable) {
					const shiftHour = formatDate({
						dateStr: params.row.shiftStartDateTime,
						formatType: "TIME_RANGE",
						endDateStr: params.row.shiftEndDateTime,
					});

					return (
						<Tooltip title={shiftHour}>
							<span
								style={{
									textOverflow: "ellipsis",
									overflow: "hidden",
								}}
							>
								<Highlight
									searchText={search}
									value={shiftHour !== "" ? shiftHour : "-"}
								/>
							</span>
						</Tooltip>
					);
				}
				return "-";
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					shiftStartDateTime: string;
					shiftEndDateTime: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					const endDate = formatDate({
						dateStr: row.shiftStartDateTime,
						formatType: "TIME_RANGE",
						endDateStr: row.shiftEndDateTime,
					});
					return endDate + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "shiftLocation",
			headerName: t("worker-activity_shift-location"),
			flex: 1,
			minWidth: 120,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_shift-location")}</Typography>
			),
			renderCell: (params) => {
				const shiftLocation = params.row.shiftLocationName;
				return (
					<Tooltip title={shiftLocation}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={shiftLocation !== "" ? shiftLocation : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					shiftLocationName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.shiftLocationName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "workerAvailability",
			headerName: t("worker-activity_worker-hours"),
			flex: 1,
			minWidth: 150,
			width: 150,
			headerAlign: "center",
			valueGetter: (_, row) => {
				const { shiftStartDateTime, shiftEndDateTime, workerShiftStartDateTime, workerShiftEndDateTime } = row;
		
				if (!shiftStartDateTime || !shiftEndDateTime || !workerShiftStartDateTime || !workerShiftEndDateTime) {
					return { availabilityType: 0 }; // No availability
				}
		
				if (workerShiftStartDateTime === shiftStartDateTime && workerShiftEndDateTime === shiftEndDateTime) {
					return { availabilityType: 3 }; // Full Shift
				}
				if (workerShiftStartDateTime === shiftStartDateTime) {
					return { availabilityType: 2 }; // First Half
				}
				if (workerShiftEndDateTime === shiftEndDateTime) {
					return { availabilityType: 1 }; // Second Half
				}
		
				return { availabilityType: 0 };
			},
			getSortComparator(sortDirection) {
				return (v1, v2) => {
					// Sorting order mapping
					const availabilityPriority = {
						asc: [2, 1, 3],   // first half, second half, full shift
						desc: [3, 2, 1]   // full shift, first half, second half
					};
			
					const priority = availabilityPriority[sortDirection || "asc"];
					
					const v1Index = priority.indexOf(v1.availabilityType);
					const v2Index = priority.indexOf(v2.availabilityType);
			
					return v1Index - v2Index;
				};
			},
			renderHeader: () => (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "24px",
							letterSpacing: "0.17px",
							color: "#000000DE",
						}}
					>{t("worker-activity_worker-hours")}</Typography>
				</Box>
			),
			renderCell: (params) => {
				if (
					params.row.shiftStartDateTime &&
					params.row.shiftEndDateTime &&
					params.row.workerShiftStartDateTime &&
					params.row.workerShiftEndDateTime
				) {
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
							}}
						>
							<Box
								sx={{
									maxWidth: "125px",
								}}
							>
								<TimeRangeBar
									totalStartTime={params?.row?.shiftStartDateTime}
									totalEndTime={params?.row?.shiftEndDateTime}
									availableStartTime={params?.row?.workerShiftStartDateTime}
									availableEndTime={params?.row?.workerShiftEndDateTime}
								/>
							</Box>
						</Box>
					);
				}
				return (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
						}}
					>
						-
					</Box>
				);
			},
		},
		{
			field: "workcenter",
			headerName: t("worker-activity_work-center"),
			flex: 1,
			minWidth: 165,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_work-center")}</Typography>
			),
			renderCell: (params) => {
				const workcenter = params.row.shiftWorkcenterName;
				return (
					<Tooltip title={workcenter}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workcenter ? workcenter : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					shiftWorkcenterName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.shiftWorkcenterName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "publishedByName",
			headerName: t("worker-activity_published-by"),
			flex: 1,
			minWidth: 165,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_published-by")}</Typography>
			),
			renderCell: (params) => {
				const publishedByName = params.row.publishedByName;
				return (
					<Tooltip title={publishedByName}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={publishedByName !== "" ? publishedByName : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					publishedByName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.publishedByName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "reportingManagerName",
			headerName: t("worker-activity_reporting-manager"),
			flex: 1,
			minWidth: 165,
			hideable: true,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_reporting-manager")}</Typography>
			),
			renderCell: (params) => {
				const reportingManagerName = params.row.reportingManagerName;
				return (
					<Tooltip title={reportingManagerName}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={reportingManagerName !== "" ? reportingManagerName : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					reportingManagerName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.reportingManagerName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "jobRoleSkills",
			headerName: t("worker-activity_job-role-skills-required"),
			flex: 1,
			minWidth: 200,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_job-role-skills-required")}</Typography>
			),
			renderCell: (params) => {
				const jobName = params.row.jobName;
				const skills = params.row.skills
					? params.row.skills
							.map((skill: { skillName: string }) => skill.skillName)
							.join(", ")
					: "";
				if (jobName) {
					return (
						<Box sx={{ marginTop: "10px" }}>
							<JobRoleChip name={jobName} color={params.row.jobColor} />
						</Box>
					);
				}
				return (
					<Tooltip title={skills}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={skills !== "" ? skills : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					jobName: string;
					skills: { skillName: string }[];
					id: string;
				}
			) => {
				const jobName = row.jobName;
				const skills = row.skills
					? row.skills
							.map((skill: { skillName: string }) => skill.skillName)
							.join(", ")
					: "";
				if (row.isShiftAvailable) {
					if (jobName) {
						return jobName + " " + row.id;
					}
					return skills + " " + row.id;
				}
				return row.id;
			},
		},
		{
			field: "status",
			headerName: t("worker-activity_status"),
			flex: 1,
			minWidth: 165,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_status")}</Typography>
			),
			renderCell: (params) => {
				const shiftAttendanceStatus = params.row.attendanceStatus.toLowerCase();
        const shiftStatus = params.row.shiftStatus.toLowerCase();
        const status = shiftAttendanceStatus === "c" || shiftStatus === "c" ? "cancelled" : shiftAttendanceStatus;
				return (
					<>
            {/* isPast logic was implemented because future shifts are coming with p status */}
						{(isPast(new Date(params.row.shiftEndDateTime)) || status === "cancelled") ? (
							<Box display='flex' alignItems='center' ml={1.25} mt={2}>
								{statusObj[status]?.icon}
								<Typography
									align='center'
									variant='subtitle2'
									ml={1}
									flexWrap='wrap'
								>
									<Highlight
										value={statusObj[status]?.title}
										searchText={search}
									/>
								</Typography>
							</Box>
						) : (
							<>--</>
						)}
					</>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					attendanceStatus: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.attendanceStatus + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "cancellationStatus",
			headerName: t("worker-activity_cancellation-status"),
			flex: 1,
			minWidth: 165,
			hideable: true,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_cancellation-status")}</Typography>
			),
			renderCell: (params) => {
				const isCancelOK = params.row.isCancelOK;
				if (isCancelOK === null || isCancelOK === undefined) {
					return <>--</>;
				}
				return (
					<Box display='flex' alignItems='center' ml={1.25} mt={2}>
						{isCancelOK ? (
							<>
								<AlarmOnIcon color='success' />
								<Typography variant='subtitle2' ml={1}>{t("worker-activity_in-time")}</Typography>
							</>
						) : (
							<Tooltip title={params.row.cancelViolationReason || ""}>
								<Box display='flex' alignItems='center'>
									<DoDisturbOutlinedIcon color='error' />
									<Typography variant='subtitle2' ml={1}>{t("worker-activity_late")}</Typography>
								</Box>
							</Tooltip>
						)}
					</Box>
				);
			},
			valueGetter: (
				_: string,
				row: { isCancelOK: boolean | null; shiftId: string; id: string }
			) => {
				if (row.isCancelOK === null || row.isCancelOK === undefined) {
					return "-" + row.shiftId + "-" + row.id;
				}
				return (row.isCancelOK ? "In Time" : "Late") + "-" + row.shiftId + "-" + row.id;
			},
		},
		{
			field: "workerLocation",
			headerName: t("worker-activity_worker-location"),
			flex: 1,
			minWidth: 130,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_worker-location")}</Typography>
			),
			renderCell: (params) => {
				const workerLocationName = params.row.workerLocationName;
				return (
					<Tooltip title={workerLocationName}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workerLocationName !== "" ? workerLocationName : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					workerLocationName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.workerLocationName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "workerWorkcenter",
			headerName: t("worker-activity_worker-work-center"),
			flex: 1,
			minWidth: 150,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_worker-work-center")}</Typography>
			),
			renderCell: (params) => {
				const workerWorkCenter = params.row.workerWorkCenterName;
				return (
					<Tooltip title={workerWorkCenter}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={workerWorkCenter !== "" ? workerWorkCenter : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					workerWorkCenterName: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.workerWorkCenterName + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
		{
			field: "supervisorComments",
			headerName: t("worker-activity_supervisor-comments"),
			flex: 1,
			minWidth: 200,
			renderHeader: () => (
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "24px",
						letterSpacing: "0.17px",
						color: "#000000DE",
					}}
				>{t("worker-activity_supervisor-comments")}</Typography>
			),
			renderCell: (params) => {
				const supervisorComments = params.row.supervisorComments;
				return (
					<Tooltip title={supervisorComments}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
							}}
						>
							<Highlight
								searchText={search}
								value={supervisorComments ? supervisorComments : "-"}
							/>
						</span>
					</Tooltip>
				);
			},
			valueGetter: (
				_: string,
				row: {
					isShiftAvailable: boolean;
					supervisorComments: string;
					shiftId: string;
					id: string;
				}
			) => {
				if (row.isShiftAvailable) {
					return row.supervisorComments + "-" + row.shiftId + "-" + row.id;
				}
				return row.id;
			},
		},
	];

	return columns;
};

export default useDayActivityColumn;
